<template>
  <section class="asc-role-dialog--container">
    <asc-dialog
      class="asc-role-dialog"
      :text="title"
      :width="900"
      :show-dialog="showDialog"
      primary-button-text="Apply"
      :on-primary="onApply"
      :primary-available="valid"
      :on-close="onCloseDialog"
    >
      <template v-if="showDialog && !edit">
        <asc-spinner-wrapper :loading="loading">
          <asc-role-dialog-form
            @input="handleFormChanged"
          ></asc-role-dialog-form>
          <asc-role-dialog-grid-template
            title="Role Editor"
            :values="mappedResourceData"
            :grid-columns="getGridColumnDefs"
            @onRoleGridReady="handleRoleGridReady"
          ></asc-role-dialog-grid-template>
        </asc-spinner-wrapper>
      </template>

      <template v-else>
        <asc-spinner-wrapper :loading="loading">
          <asc-role-dialog-form
            :role="role"
            @input="handleFormChanged"
          ></asc-role-dialog-form>
          <asc-role-dialog-grid-template
            title="Role Editor"
            :values="mappedResourceData"
            :grid-columns="getGridColumnDefs"
            @onRoleGridReady="handleRoleGridReady"
          ></asc-role-dialog-grid-template>
        </asc-spinner-wrapper>
      </template>
    </asc-dialog>
  </section>
</template>

<script>
import AscDialog from '@/components/atoms/AscDialog'
import AscSpinnerWrapper from '@/components/atoms/AscSpinnerWrapper'
import AscRoleDialogForm from './AscRoleDialogForm'
import AscRoleDialogGridTemplate from '@/components/templates/AscRoleDialogGridTemplate'
import { stringTextMatcher } from '@/components/agGrid/AgGridHelperMethods'
import {
  AscTooltipCellRenderer,
  AscCheckboxCellRenderer,
} from '@/components/agGrid'
import { AscGridMethodsMixin } from '@/components/mixins'

export default {
  name: 'AscRoleDialog',

  components: {
    AscDialog,
    AscSpinnerWrapper,
    AscRoleDialogForm,
    AscRoleDialogGridTemplate,
    AscTooltipCellRenderer,
    AscCheckboxCellRenderer,
  },

  mixins: [AscGridMethodsMixin],

  props: {
    onPrimary: {
      type: Function,
      default: () => {},
    },
    primaryAvailable: {
      type: Boolean,
      default: false,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    showDialog: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: true,
    },
    edit: {
      type: Boolean,
      default: false,
    },
    role: {
      type: Object,
      default: () => {},
    },
    mappedResourceData: {
      type: Array,
      default: () => [],
    },
    allResourceModules: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      valid: false,
      newRole: {
        resources: [],
      },
      newPermissions: [],
      roleGridApi: null,
    }
  },

  computed: {
    roleFieldsValid() {
      if (this.newRole) {
        return !!(this.newRole.name && this.newRole.description)
      }
      return false
    },
    roleFieldsChanged() {
      if (this.newRole && this.role) {
        return !(JSON.stringify(this.newRole) === JSON.stringify(this.role))
      }
      return false
    },
    validate() {
      return this.roleFieldsValid
    },
    getGridColumnDefs() {
      return [
        {
          headerName: 'Module',
          field: 'moduleName',
          rowGroup: true,
          hide: true,
          valueGetter: params => {
            if (params.data && params.data.resourceModuleId) {
              const resourceModule = this.allResourceModules.filter(
                item => params.data.resourceModuleId === item.id,
              )

              return resourceModule[0].name
            } else {
              return 'Other'
            }
          },
        },
        {
          headerName: 'Resource',
          field: 'displayName',
          suppressMovable: true,
          resizable: false,
          sortable: true,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          filterValueGetter: params => params.data.displayName,
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('displayName'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: 'Read',
          field: 'readPermission',
          suppressMovable: true,
          width: 90,
          maxWidth: 100,
          resizable: false,
          sortable: true,
          suppressSizeToFit: true,
          cellActiveHighlight: true,
          cellRenderer: 'AscCheckboxCellRenderer',
          cellRendererParams: {
            valuefield: 'readPermission',
            callback: arg => this.handleGridCheckboxToggle(arg),
          },
          cellClass: ['ag-cell--flex'],
          headerClass: ['text-center'],
        },
        {
          headerName: 'Edit',
          field: 'editPermission',
          suppressMovable: true,
          width: 90,
          maxWidth: 100,
          resizable: false,
          sortable: true,
          suppressSizeToFit: true,
          cellActiveHighlight: true,
          cellRenderer: 'AscCheckboxCellRenderer',
          cellRendererParams: {
            valuefield: 'editPermission',
            callback: arg => this.handleGridCheckboxToggle(arg),
          },
          cellClass: ['ag-cell--flex'],
          headerClass: ['text-center'],
        },
        {
          headerName: 'Delete',
          field: 'deletePermission',
          suppressMovable: true,
          width: 90,
          maxWidth: 100,
          resizable: false,
          sortable: true,
          suppressSizeToFit: true,
          cellActiveHighlight: true,
          cellRenderer: 'AscCheckboxCellRenderer',
          cellRendererParams: {
            valuefield: 'deletePermission',
            callback: arg => this.handleGridCheckboxToggle(arg),
          },
          cellClass: ['ag-cell--flex'],
          headerClass: ['text-center'],
        },
      ]
    },
  },

  watch: {
    mappedResourceData() {
      if (this.roleGridApi) this.roleGridApi.expandAll()
    },
  },

  methods: {
    onCloseDialog() {
      this.onClose()
      this.valid = false
      this.newPermissions = []
    },
    onApply() {
      this.valid = false
      if (this.edit) {
        this.submitEdit()
      } else {
        this.submitNew()
      }
      this.onCloseDialog()
    },
    submitEdit() {
      this.$emit('submitApply', this.newRole, this.newPermissions)
    },
    submitNew() {
      this.$emit('submitApply', this.newRole, this.newPermissions)
    },
    handleFormChanged(formData) {
      this.newRole = formData.fields
      this.valid = this.validate
    },
    handleGridCheckboxToggle(val) {
      if (this.newPermissions.length === 0) {
        this.newPermissions = this.mappedResourceData.map(x => x)
      }

      this.newPermissions = this.newPermissions.map(x =>
        x.id === val.id ? { ...x, ...val } : x,
      )
    },
    handleRoleGridReady(api) {
      if (api) this.roleGridApi = api
    },
  },
}
</script>

<style lang="scss"></style>
